import { isMemoryArchiveType } from "@/lib/leaderMemoryVisibility";
import { useMemo } from "react";
import { format } from "date-fns";
import { isTranscriptAnalyzedStatus } from "@/lib/transcriptStatus";
import {
  buildThroughlineArcPoints,
  buildThroughlineDayNarrative,
  buildThroughlineGapAlert,
  describeThroughlineConfidence,
  rankThroughlineMilestones,
  type ThroughlineNodeState,
  type ThroughlineLedgerEventInput,
} from "@/lib/throughlineAdapter";

export type ThroughlineMilestone = {
  id: string;
  at: string;
  type: "transcript" | "reflection" | "conversation" | "veera";
  label: string;
  detail: string;
  link?: string | null;
};

export type HistoryEvidenceDetail = {
  behaviorNames: string[];
  strengths: string[];
  watchFors: string[];
  veeraSignals: string[];
  gapNotice: string | null;
  sourceNotes: string | null;
};

export type ThroughlineDayRollup = {
  dayKey: string;
  label: string;
  meetingCount: number;
  state: ThroughlineNodeState;
  evidence: number;
  agreement: number;
  momentum: number;
  alignment: "aligned" | "partial" | "gap";
  count: number;
  confidence: "high" | "medium" | "low";
  confidenceReason: string;
  topBehaviors: string[];
  sourceCounts: Record<ThroughlineMilestone["type"], number>;
  metaBehaviorHeadline: string;
  summary: string;
  drivers: string[];
  itemsByType: Record<ThroughlineMilestone["type"], ThroughlineMilestone[]>;
};

type PracticeSummarySignal = {
  id: string;
  meeting_id: string | null;
  meeting_title: string | null;
  meeting_start_time: string | null;
  behavior_name: string | null;
  transcript_summary: string | null;
  practice_title: string | null;
  practice_instruction: string | null;
  has_transcript_insights: boolean | null;
};

type VeeraObservation = {
  id: string;
  created_at: string;
  snippet: string;
  source_type: string;
};

type ConversationRecord = {
  id: string;
  source: string;
  created_at: string;
  summary: string;
};

type SharedHistoryItem = {
  id: string;
  type: "session" | "reflection" | "shared_reflection" | "transcript" | "artifact";
  source: string;
  timestamp: string;
  title: string;
  analysis_status?: string | null;
  meeting_id?: string | null;
  behavior_name?: string | null;
  transcript_summary?: string | null;
  practice_instruction?: string | null;
  rawContent?: string | null;
  rawLink?: string | null;
  rawLinkLabel?: string | null;
  artifactPath?: { bucket: string; path: string } | null;
};

type TranscriptStats = {
  total: number;
  analyzed: number;
  withSummary: number;
};

type ThroughlineLedgerEvent = ThroughlineLedgerEventInput & {
  metadata?: Record<string, unknown> | null;
};

export type UseThroughlinePresentationInput = {
  data?: {
    practiceSummarySignals?: PracticeSummarySignal[];
    transcriptByMeeting?: Map<string, { id: string; created_at: string }>;
    ledgerEvents?: ThroughlineLedgerEvent[];
    veeraObservationSignals?: VeeraObservation[];
    transcriptStats?: TranscriptStats;
  } | null;
  historyItems: SharedHistoryItem[];
  recentConversationRecords: ConversationRecord[];
  agreementLines: string[];
  throughlineWindowDays: 15 | 30 | 60 | 90;
  selectedThroughlineDay: string | null;
};

function throughlineDayKeyFromPointId(pointId: string) {
  return pointId.startsWith("bucket-") ? pointId.replace("bucket-", "") : null;
}

function normalizeInsightLine(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function trimmedNote(value: string | null | undefined, maxLength = 380) {
  if (!value) return null;
  const normalized = normalizeInsightLine(value);
  if (!normalized) return null;
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength).trim()}...` : normalized;
}

export function useThroughlinePresentation({
  data,
  historyItems,
  recentConversationRecords,
  agreementLines,
  throughlineWindowDays,
  selectedThroughlineDay,
}: UseThroughlinePresentationInput) {
  const transcriptBehaviorSignals = useMemo(() => {
    const signals = data?.practiceSummarySignals || [];
    return signals
      .filter((item) => Boolean(item.has_transcript_insights || item.transcript_summary))
      .slice(0, 4);
  }, [data?.practiceSummarySignals]);

  const veeraObservationSignals = useMemo(() => {
    const signals = data?.veeraObservationSignals || [];
    return signals.slice(0, 3);
  }, [data?.veeraObservationSignals]);

  const unifiedContinuitySummary = useMemo(() => {
    const keyAgreement = agreementLines[0] || null;
    const recentCoachSignal = recentConversationRecords[0]?.summary || null;
    const recentVeeraSignal = veeraObservationSignals[0]?.snippet || null;

    const highlights = [
      keyAgreement ? `Agreement focus: ${keyAgreement}` : null,
      recentCoachSignal ? `Coach/leader signal: ${recentCoachSignal}` : null,
      recentVeeraSignal ? `Veera context: ${recentVeeraSignal}` : null,
    ].filter((line): line is string => Boolean(line));

    if (highlights.length === 0) {
      return {
        headline: "No continuity summary yet.",
        highlights: ["As transcripts, reflections, and coach exchanges accumulate, this summary will capture the thread across all sources."],
      };
    }

    return {
      headline: "Unified continuity summary",
      highlights: highlights.slice(0, 3),
    };
  }, [agreementLines, recentConversationRecords, veeraObservationSignals]);

  const transcriptStats = useMemo(
    () => data?.transcriptStats || { total: 0, analyzed: 0, withSummary: 0 },
    [data?.transcriptStats],
  );

  const behaviorCoverage = useMemo(() => {
    const signals = data?.practiceSummarySignals || [];
    const counts = new Map<string, number>();
    signals.forEach((signal) => {
      const key = signal.behavior_name?.trim();
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([behavior, count]) => ({ behavior, count }))
      .sort((a, b) => b.count - a.count);
  }, [data?.practiceSummarySignals]);

  const throughlineMilestones = useMemo(() => {
    const milestones: ThroughlineMilestone[] = [];
    const seenMilestoneIds = new Set<string>();
    const seenMilestoneSignatures = new Set<string>();
    const buildMilestoneSignature = (milestone: ThroughlineMilestone) => {
      const dayKey = format(new Date(milestone.at), "yyyy-MM-dd");
      return [
        dayKey,
        milestone.type,
        normalizeInsightLine(milestone.detail).toLowerCase(),
      ].join("::");
    };
    const pushMilestone = (milestone: ThroughlineMilestone) => {
      if (seenMilestoneIds.has(milestone.id)) return;
      const signature = buildMilestoneSignature(milestone);
      if (seenMilestoneSignatures.has(signature)) return;
      seenMilestoneIds.add(milestone.id);
      seenMilestoneSignatures.add(signature);
      milestones.push(milestone);
    };

    const summarySignals = (data?.practiceSummarySignals || [])
      .filter((item) => Boolean(item.meeting_start_time));
    const renderedSummaryMeetingIds = new Set(
      summarySignals
        .map((item) => item.meeting_id)
        .filter((meetingId): meetingId is string => Boolean(meetingId)),
    );

    summarySignals.forEach((item) => {
      const transcriptId =
        item.meeting_id && data?.transcriptByMeeting instanceof Map
          ? data.transcriptByMeeting.get(item.meeting_id)?.id
          : null;
      pushMilestone({
        id: `summary-${item.id}-timeline`,
        at: item.meeting_start_time || new Date().toISOString(),
        type: "transcript",
        label: item.behavior_name || "Practice signal",
        detail: item.meeting_title || "Meeting",
        link: transcriptId ? `/transcript-analysis/${transcriptId}` : null,
      });
    });

    historyItems.forEach((item) => {
      if (
        item.type === "transcript" &&
        isTranscriptAnalyzedStatus(item.analysis_status) &&
        (!item.meeting_id || !renderedSummaryMeetingIds.has(item.meeting_id))
      ) {
        pushMilestone({
          id: `${item.id}-timeline`,
          at: item.timestamp || new Date().toISOString(),
          type: "transcript",
          label: item.behavior_name || "Transcript insight",
          detail: item.transcript_summary || item.title || "Analyzed transcript",
          link: item.rawLink || null,
        });
      }

      if (item.type === "reflection") {
        pushMilestone({
          id: `${item.id}-timeline`,
          at: item.timestamp || new Date().toISOString(),
          type: "reflection",
          label: "Reflection",
          detail: item.behavior_name || "Leader reflection",
          link: item.rawLink || null,
        });
      }
    });

    recentConversationRecords.slice(0, 30).forEach((record) => {
      pushMilestone({
        id: `${record.id}-timeline`,
        at: record.created_at,
        type: "conversation",
        label: record.source,
        detail: record.summary.slice(0, 120),
      });
    });

    veeraObservationSignals.slice(0, 20).forEach((item) => {
      pushMilestone({
        id: `${item.id}-timeline`,
        at: item.created_at,
        type: "veera",
        label: "Veera observation",
        detail: item.snippet.slice(0, 120),
      });
    });

    (data?.ledgerEvents || []).filter(event => !isMemoryArchiveType(event.sourceType)).slice(0, 220).forEach((event) => {
      const snippet =
        typeof event.metadata?.snippet === "string" && event.metadata.snippet.trim().length > 0
          ? event.metadata.snippet.trim().slice(0, 120)
          : `${event.eventType.replace(/_/g, " ")} recorded`;
      const source = event.sourceType.toLowerCase();
      const type: ThroughlineMilestone["type"] =
        source.includes("transcript") || source.includes("practice") || source.includes("assessment")
          ? "transcript"
          : source.includes("reflection")
              ? "reflection"
              : source.includes("coach")
                ? "conversation"
              : "veera";

      pushMilestone({
        id: `ledger-${event.id}-timeline`,
        at: event.at,
        type,
        label: type === "transcript" ? "Context evidence" : type === "reflection" ? "Reflection signal" : type === "conversation" ? "Coaching signal" : "Veera observation",
        detail: snippet,
      });
    });

    return milestones
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
      .slice(0, 300);
  }, [data?.practiceSummarySignals, data?.transcriptByMeeting, data?.ledgerEvents, historyItems, recentConversationRecords, veeraObservationSignals]);

  const historyEvidenceDetails = useMemo(() => {
    const details = new Map<string, HistoryEvidenceDetail>();
    const practiceSignals = data?.practiceSummarySignals || [];
    const veeraSignals = data?.veeraObservationSignals || [];
    const ledgerSignals = (data?.ledgerEvents || []).filter(event => !isMemoryArchiveType(event.sourceType));

    historyItems.forEach((item) => {
      const dayKey = format(new Date(item.timestamp), "yyyy-MM-dd");
      const daySignals = practiceSignals.filter((signal) => {
        if (!signal.meeting_start_time) return false;
        if (format(new Date(signal.meeting_start_time), "yyyy-MM-dd") !== dayKey) return false;
        if (item.meeting_id && signal.meeting_id) return signal.meeting_id === item.meeting_id;
        return true;
      });

      const behaviorNames = Array.from(
        new Set(
          daySignals
            .map((signal) => signal.behavior_name?.trim())
            .filter((value): value is string => Boolean(value)),
        ),
      );

      const strengths = Array.from(
        new Set(
          daySignals
            .map((signal) => trimmedNote(signal.transcript_summary, 220))
            .filter((value): value is string => Boolean(value)),
        ),
      ).slice(0, 2);

      const watchFors = Array.from(
        new Set(
          daySignals
            .map((signal) => trimmedNote(signal.practice_instruction, 180))
            .filter((value): value is string => Boolean(value)),
        ),
      ).slice(0, 2);

      const dayVeeraSignals = veeraSignals
        .filter((signal) => format(new Date(signal.created_at), "yyyy-MM-dd") === dayKey)
        .map((signal) => trimmedNote(signal.snippet, 220))
        .filter((value): value is string => Boolean(value))
        .slice(0, 2);

      const ledgerSnippets = ledgerSignals
        .filter((event) => format(new Date(event.at), "yyyy-MM-dd") === dayKey)
        .map((event) => {
          const raw = event.metadata && typeof event.metadata.snippet === "string" ? event.metadata.snippet : null;
          return trimmedNote(raw, 220);
        })
        .filter((value): value is string => Boolean(value))
        .slice(0, 2);

      const hasTranscriptEvidence = daySignals.some((signal) => Boolean(signal.has_transcript_insights || signal.transcript_summary));
      const gapNotice =
        daySignals.length > 0 && !hasTranscriptEvidence
          ? `No transcript-confirmed behavior evidence was detected for this day across ${daySignals.length} meeting${daySignals.length === 1 ? "" : "s"}.`
          : null;

      const sourceNotes = item.rawContent
        ? item.type === "transcript"
          ? "Transcript source is available. Use Open source for full transcript context."
          : trimmedNote(item.rawContent, 280)
        : null;

      details.set(item.id, {
        behaviorNames,
        strengths,
        watchFors,
        veeraSignals: Array.from(new Set([...dayVeeraSignals, ...ledgerSnippets])).slice(0, 2),
        gapNotice,
        sourceNotes,
      });
    });

    return details;
  }, [data?.ledgerEvents, data?.practiceSummarySignals, data?.veeraObservationSignals, historyItems]);

  const throughlineModel = useMemo(
    () =>
      buildThroughlineArcPoints({
        milestones: throughlineMilestones,
        ledgerEvents: data?.ledgerEvents || [],
        transcriptStats,
        requestedWindowDays: throughlineWindowDays,
      }),
    [throughlineMilestones, data?.ledgerEvents, transcriptStats, throughlineWindowDays],
  );

  const throughlineArcPoints = throughlineModel.points;
  const throughlineGapAlert = useMemo(() => buildThroughlineGapAlert(throughlineArcPoints, 7), [throughlineArcPoints]);
  const sparseWindowAutoExpanded = throughlineModel.sparseWindowAutoExpanded;
  const throughlineStateCounts = throughlineModel.stateCounts;

  const chartLabelPoints = useMemo(() => {
    const displayedWindowDays = throughlineModel.effectiveWindowDays;
    const labelStep = displayedWindowDays <= 15 ? 3 : displayedWindowDays <= 30 ? 5 : displayedWindowDays <= 60 ? 10 : 15;
    return throughlineArcPoints.filter((point, index) => index % labelStep === 0 || index === throughlineArcPoints.length - 1);
  }, [throughlineArcPoints, throughlineModel.effectiveWindowDays]);

  const throughlineDayRollups = useMemo(() => {
    const inScopeDayKeys = new Set(
      throughlineArcPoints.map((point) => throughlineDayKeyFromPointId(point.id)).filter(Boolean) as string[],
    );
    const milestonesByDay = new Map<string, ThroughlineMilestone[]>();
    throughlineMilestones.forEach((milestone) => {
      const dayKey = format(new Date(milestone.at), "yyyy-MM-dd");
      if (!inScopeDayKeys.has(dayKey)) return;
      const existing = milestonesByDay.get(dayKey) || [];
      existing.push(milestone);
      milestonesByDay.set(dayKey, existing);
    });

    const behaviorCountsByDay = new Map<string, Map<string, number>>();
    const meetingsByDay = new Map<string, Set<string>>();
    (data?.practiceSummarySignals || []).forEach((signal) => {
      if (!signal.meeting_start_time || !signal.behavior_name) return;
      const dayKey = format(new Date(signal.meeting_start_time), "yyyy-MM-dd");
      if (!inScopeDayKeys.has(dayKey)) return;
      const counts = behaviorCountsByDay.get(dayKey) || new Map<string, number>();
      counts.set(signal.behavior_name, (counts.get(signal.behavior_name) || 0) + 1);
      behaviorCountsByDay.set(dayKey, counts);

      if (signal.meeting_id) {
        const existingMeetings = meetingsByDay.get(dayKey) || new Set<string>();
        existingMeetings.add(signal.meeting_id);
        meetingsByDay.set(dayKey, existingMeetings);
      }
    });

    const rollups = new Map<string, ThroughlineDayRollup>();
    throughlineArcPoints.forEach((point) => {
      const dayKey = throughlineDayKeyFromPointId(point.id);
      if (!dayKey) return;
      const dayItems = rankThroughlineMilestones(milestonesByDay.get(dayKey) || []);
      const itemsByType: Record<ThroughlineMilestone["type"], ThroughlineMilestone[]> = {
        transcript: rankThroughlineMilestones(dayItems.filter((item) => item.type === "transcript")),
        reflection: rankThroughlineMilestones(dayItems.filter((item) => item.type === "reflection")),
        conversation: rankThroughlineMilestones(dayItems.filter((item) => item.type === "conversation")),
        veera: rankThroughlineMilestones(dayItems.filter((item) => item.type === "veera")),
      };
      const behaviorCounts = behaviorCountsByDay.get(dayKey);
      const meetingCount = meetingsByDay.get(dayKey)?.size || 0;
      const topBehaviors = behaviorCounts
        ? Array.from(behaviorCounts.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([behavior]) => behavior)
        : [];
      const narrative = buildThroughlineDayNarrative({
        state: point.state,
        alignment: point.alignment,
        count: point.count,
        evidence: point.evidence,
        agreement: point.agreement,
        momentum: point.momentum,
        topBehaviors,
        sourceCounts: {
          transcript: itemsByType.transcript.length,
          reflection: itemsByType.reflection.length,
          conversation: itemsByType.conversation.length,
          veera: itemsByType.veera.length,
        },
        isEmpty: point.isEmpty,
      });
      const confidence: ThroughlineDayRollup["confidence"] =
        point.evidence >= 0.7 && point.count >= 3
          ? "high"
          : point.evidence >= 0.5 && point.count >= 2
            ? "medium"
            : "low";
      const confidenceReason = describeThroughlineConfidence({
        confidence,
        count: point.count,
        evidence: point.evidence,
      });
      rollups.set(dayKey, {
        dayKey,
        label: point.label,
        state: point.state,
        evidence: point.evidence,
        agreement: point.agreement,
        momentum: point.momentum,
        alignment: point.alignment,
        count: point.count,
        confidence,
        confidenceReason,
        topBehaviors,
        meetingCount,
        sourceCounts: {
          transcript: itemsByType.transcript.length,
          reflection: itemsByType.reflection.length,
          conversation: itemsByType.conversation.length,
          veera: itemsByType.veera.length,
        },
        metaBehaviorHeadline: narrative.headline,
        summary: meetingCount > 1 && !point.isEmpty ? `Across ${meetingCount} meetings, ${narrative.summary.charAt(0).toLowerCase()}${narrative.summary.slice(1)}` : narrative.summary,
        drivers: narrative.drivers,
        itemsByType,
      });
    });
    return rollups;
  }, [throughlineArcPoints, throughlineMilestones, data?.practiceSummarySignals]);

  const selectedThroughlineRollup = selectedThroughlineDay
    ? throughlineDayRollups.get(selectedThroughlineDay) || null
    : null;

  return {
    transcriptBehaviorSignals,
    veeraObservationSignals,
    unifiedContinuitySummary,
    transcriptStats,
    behaviorCoverage,
    throughlineMilestones,
    historyEvidenceDetails,
    throughlineModel,
    throughlineArcPoints,
    throughlineGapAlert,
    sparseWindowAutoExpanded,
    throughlineStateCounts,
    chartLabelPoints,
    throughlineDayRollups,
    selectedThroughlineRollup,
  };
}
